import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

// ─── Design tokens ────────────────────────────────────────────────────────────
export const theme = {
  // Colors
  green900:   '#0f2210',
  green800:   '#1a3d1a',
  green700:   '#1e4d1e',
  green600:   '#2d6a2d',
  green500:   '#3a8a3a',
  green400:   '#4fa84f',
  green300:   '#7dc47d',
  green100:   '#e8f5e8',
  green50:    '#f2faf2',

  white:      '#ffffff',
  bg:         '#f0f2f0',
  border:     '#e4ebe4',
  borderDark: '#c8d4c8',

  text900:    '#1a2e1a',
  text700:    '#2d4a2d',
  text500:    '#4a6a4a',
  text400:    '#6b8a6b',
  text300:    '#9aaf9a',

  shadow:     '0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04)',
  shadowMd:   '0 4px 12px rgba(0,0,0,0.09), 0 2px 4px rgba(0,0,0,0.05)',
  shadowLg:   '0 10px 28px rgba(0,0,0,0.12), 0 4px 8px rgba(0,0,0,0.06)',

  radius:     '10px',
  radiusSm:   '7px',
  radiusLg:   '14px',

  font:       "'DM Sans', sans-serif",
}

const NAV_ADMIN = [
  { path: '/',           label: 'Dashboard',   icon: HomeIcon },
  { path: '/jobs',       label: 'Jobs',         icon: JobsIcon },
  { path: '/clients',    label: 'Clients',      icon: ClientsIcon },
  { path: '/employees',  label: 'Employees',    icon: EmployeesIcon },
  { path: '/clock',      label: 'Clock In/Out', icon: ClockIcon },
  { path: '/schedule',   label: 'Schedule',     icon: ScheduleIcon },
  { path: '/site-visits', label: 'Site Visits', icon: SiteVisitsIcon },
  { path: '/estimates',  label: 'Estimates',    icon: EstimatesIcon },
  { path: '/proposals',  label: 'Proposals',    icon: ProposalsIcon },
  { path: '/invoices',   label: 'Invoices',     icon: InvoicesIcon },
  { path: '/financials', label: 'Financials',   icon: FinancialsIcon },
]

const NAV_EMPLOYEE = [
  { path: '/',         label: 'Dashboard',   icon: HomeIcon },
  { path: '/jobs',     label: 'My Jobs',     icon: JobsIcon },
  { path: '/schedule', label: 'My Schedule', icon: ScheduleIcon },
  { path: '/clients',  label: 'Clients',     icon: ClientsIcon },
  { path: '/site-visits', label: 'Site Visits', icon: SiteVisitsIcon },
  { path: '/clock',    label: 'Clock In/Out',icon: ClockIcon },
]

// Mobile is a fast in-field tool — persistent bottom nav for the handful of
// things reached constantly, everything else lives one tap away in "More"
// (the existing full nav drawer). See Docs/UI_GUIDE.md "Mobile vs Desktop Purpose".
const BOTTOM_TABS_ADMIN = [
  { path: '/',         label: 'Home',     icon: HomeIcon },
  { path: '/jobs',     label: 'Jobs',     icon: JobsIcon },
  { path: '/schedule', label: 'Schedule', icon: ScheduleIcon },
  { path: '/clock',    label: 'Clock',    icon: ClockIcon },
  { key: 'more',       label: 'More',     icon: MoreIcon, isMore: true },
]
const BOTTOM_TABS_EMPLOYEE = [
  { path: '/',         label: 'Home',     icon: HomeIcon },
  { path: '/jobs',     label: 'Jobs',     icon: JobsIcon },
  { path: '/schedule', label: 'Schedule', icon: ScheduleIcon },
  { path: '/clock',    label: 'Clock',    icon: ClockIcon },
  { key: 'more',       label: 'More',     icon: MoreIcon, isMore: true },
]

export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 1024)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  return isMobile
}

export default function Layout({ children }) {
  const { profile, isAdmin, signOut } = useAuth()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const isMobile = useIsMobile()
  const nav = isAdmin ? NAV_ADMIN : NAV_EMPLOYEE
  const bottomTabs = isAdmin ? BOTTOM_TABS_ADMIN : BOTTOM_TABS_EMPLOYEE

  useEffect(() => { setMenuOpen(false) }, [location.pathname])

  const initials = (profile?.full_name || profile?.email || 'U')
    .split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  const navLinks = (
    <div style={styles.sidebarFlex}>
      <div style={styles.sidebarInner}>
        {/* Logo */}
        <div style={styles.logoArea}>
          <div style={styles.logoMark}>
            <img src="/logo.png" alt="TLC" style={styles.logoImg} />
          </div>
          <div>
            <div style={styles.brandName}>TLC Unlimited</div>
            <div style={styles.brandSub}>Field Operations</div>
          </div>
        </div>

        {/* Nav */}
        <nav style={styles.navSection}>
          {nav.map(item => {
            const active = isActive(item.path)
            const Icon = item.icon
            return (
              <Link
                key={item.path}
                to={item.path}
                style={{
                  ...styles.navItem,
                  ...(active ? styles.navItemActive : {}),
                }}
                onClick={() => setMenuOpen(false)}
              >
                <span style={{ ...styles.navIcon, ...(active ? styles.navIconActive : {}) }}>
                  <Icon />
                </span>
                <span style={styles.navLabel}>{item.label}</span>
              </Link>
            )
          })}
        </nav>
      </div>

      {/* Bottom: settings + user */}
      <div style={styles.sidebarBottom}>
        <div style={styles.navDivider} />

        {/* Client Portal link — admin only */}
        {isAdmin && (
          <Link
            to="/portal/login"
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...styles.navItem }}
            onClick={() => setMenuOpen(false)}
          >
            <span style={styles.navIcon}><PortalIcon /></span>
            <span style={styles.navLabel}>Client Portal</span>
            <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginLeft: '2px' }}>↗</span>
          </Link>
        )}

        <Link
          to="/settings"
          style={{ ...styles.navItem, ...(location.pathname === '/settings' ? styles.navItemActive : {}) }}
          onClick={() => setMenuOpen(false)}
        >
          <span style={styles.navIcon}><SettingsIcon /></span>
          <span style={styles.navLabel}>Settings</span>
        </Link>

        <div style={styles.userCard}>
          <div style={styles.userAvatar}>{initials}</div>
          <div style={styles.userInfo}>
            <div style={styles.userName}>{profile?.full_name || profile?.email}</div>
            <div style={styles.userRole}>{isAdmin ? 'Admin' : 'Employee'}</div>
          </div>
          <button style={styles.signOutBtn} onClick={signOut} title="Sign out">
            <SignOutIcon />
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div style={styles.root}>
      {/* Mobile header */}
      {isMobile && (
        <header style={styles.mobileHeader}>
          <Link to="/" style={styles.mobileBrandLink}>
            <img src="/logo.png" alt="TLC" style={styles.mobileLogoImg} />
            <span style={styles.mobileBrand}>TLC Unlimited</span>
          </Link>
          <div style={styles.mobileAvatar}>{initials}</div>
        </header>
      )}

      <div style={styles.body}>
        {isMobile ? (
          <>
            <nav style={{
              ...styles.mobileSidebar,
              transform: menuOpen ? 'translateX(0)' : 'translateX(-100%)'
            }}>
              {navLinks}
            </nav>
            {menuOpen && <div style={styles.overlay} onClick={() => setMenuOpen(false)} />}
          </>
        ) : (
          <nav style={styles.desktopSidebar}>{navLinks}</nav>
        )}

        <main style={{ ...styles.main, ...(isMobile ? styles.mainMobile : {}) }}>
          {children}
        </main>
      </div>

      {/* Bottom tab bar — mobile only, primary nav lives here now, not the header */}
      {isMobile && (
        <nav style={styles.bottomTabBar}>
          {bottomTabs.map(tab => {
            const Icon = tab.icon
            if (tab.isMore) {
              return (
                <button key="more" style={styles.tabItem} onClick={() => setMenuOpen(true)}>
                  <span style={styles.tabIcon}><Icon /></span>
                  <span style={styles.tabLabel}>{tab.label}</span>
                </button>
              )
            }
            const active = isActive(tab.path)
            return (
              <Link
                key={tab.path}
                to={tab.path}
                style={{ ...styles.tabItem, ...(active ? styles.tabItemActive : {}) }}
              >
                <span style={{ ...styles.tabIcon, ...(active ? styles.tabIconActive : {}) }}><Icon /></span>
                <span style={styles.tabLabel}>{tab.label}</span>
              </Link>
            )
          })}
        </nav>
      )}
    </div>
  )
}

/* ── Icons ── */
function HomeIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
}
function JobsIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></svg>
}
function ClientsIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
}
function EmployeesIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
}
function ClockIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
}
function ScheduleIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
}
function SiteVisitsIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
}
function EstimatesIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
}
function ProposalsIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 15l2 2 4-4"/></svg>
}
function InvoicesIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>
}
function FinancialsIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
}
function SettingsIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
}
function SignOutIcon() {
  return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
}
function PortalIcon() {
  return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
}
function MoreIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
}

/* ── Styles ── */
const styles = {
  root: {
    minHeight: '100vh',
    background: theme.bg,
    fontFamily: theme.font,
    display: 'flex',
    flexDirection: 'column',
  },

  // Mobile header
  mobileHeader: {
    background: theme.green800,
    color: 'white',
    padding: '0 16px',
    height: '56px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
  },
  mobileBrandLink: { display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' },
  mobileLogoImg: { width: '28px', height: '28px', borderRadius: '6px', objectFit: 'cover' },
  mobileBrand: { fontSize: '16px', fontWeight: '700', color: 'white' },
  mobileAvatar: {
    width: '32px', height: '32px', borderRadius: '50%',
    background: theme.green500, color: 'white',
    fontSize: '12px', fontWeight: '700',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: '2px solid rgba(255,255,255,0.25)',
  },
  // Body
  body: { display: 'flex', flex: 1, minHeight: 0 },

  // Desktop sidebar
  desktopSidebar: {
    width: '224px',
    background: theme.green800,
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    height: '100vh',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${theme.green900}`,
  },

  // Mobile sidebar
  mobileSidebar: {
    position: 'fixed',
    top: '56px', left: 0, bottom: 0,
    width: '240px',
    background: theme.green800,
    zIndex: 90,
    transition: 'transform 0.25s ease',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.45)',
    zIndex: 89,
  },

  // Sidebar internals
  sidebarFlex: { display: 'flex', flexDirection: 'column', height: '100%' },
  sidebarInner: { flex: 1, overflowY: 'auto' },
  sidebarBottom: { flexShrink: 0 },

  logoArea: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '20px 16px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    marginBottom: '6px',
  },
  logoMark: {
    width: '36px', height: '36px', borderRadius: '9px',
    background: theme.green700,
    border: `1px solid rgba(255,255,255,0.12)`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, overflow: 'hidden',
  },
  logoImg: { width: '36px', height: '36px', objectFit: 'cover' },
  brandName: { fontSize: '14px', fontWeight: '700', color: 'white', letterSpacing: '-0.1px' },
  brandSub: { fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '1px' },

  navSection: { padding: '4px 10px' },

  navItem: {
    display: 'flex', alignItems: 'center', gap: '9px',
    padding: '8px 10px',
    color: 'rgba(255,255,255,0.55)',
    textDecoration: 'none',
    fontSize: '13.5px',
    fontWeight: '500',
    borderRadius: '8px',
    margin: '1px 0',
    transition: 'all 0.15s',
  },
  navItemActive: {
    background: theme.green600,
    color: 'white',
    fontWeight: '600',
    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.1), 0 1px 3px rgba(0,0,0,0.2)`,
  },
  navIcon: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: '20px', flexShrink: 0, opacity: 0.65,
  },
  navIconActive: { opacity: 1 },
  navLabel: { flex: 1 },

  navDivider: {
    height: '1px',
    background: 'rgba(255,255,255,0.07)',
    margin: '6px 16px',
  },

  userCard: {
    display: 'flex', alignItems: 'center', gap: '9px',
    padding: '12px 14px',
    margin: '6px 10px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  userAvatar: {
    width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
    background: theme.green500,
    color: 'white', fontSize: '12px', fontWeight: '700',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: '2px solid rgba(255,255,255,0.15)',
  },
  userInfo: { flex: 1, minWidth: 0 },
  userName: {
    fontSize: '12px', fontWeight: '600', color: 'white',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  userRole: {
    fontSize: '10px', color: theme.green300, marginTop: '1px',
    textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600',
  },
  signOutBtn: {
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: 'rgba(255,255,255,0.5)',
    cursor: 'pointer', padding: '6px', borderRadius: '6px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, transition: 'all 0.15s',
  },

  // Main content
  main: {
    flex: 1,
    padding: '28px 32px',
    minWidth: 0,
    overflowY: 'auto',
  },
  mainMobile: {
    padding: '16px',
    paddingBottom: 'calc(72px + env(safe-area-inset-bottom))',
  },

  // Bottom tab bar (mobile primary nav)
  bottomTabBar: {
    position: 'fixed',
    left: 0, right: 0, bottom: 0,
    background: theme.white,
    borderTop: `1px solid ${theme.border}`,
    boxShadow: '0 -2px 10px rgba(0,0,0,0.06)',
    display: 'flex',
    paddingBottom: 'env(safe-area-inset-bottom)',
    zIndex: 80,
  },
  tabItem: {
    flex: 1,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: '3px',
    padding: '8px 4px 7px',
    background: 'none', border: 'none',
    textDecoration: 'none', cursor: 'pointer',
    color: theme.text400,
  },
  tabItemActive: { color: theme.green600 },
  tabIcon: { display: 'flex', opacity: 0.75 },
  tabIconActive: { opacity: 1 },
  tabLabel: { fontSize: '10.5px', fontWeight: '600' },
}
